import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import HandshakeIcon from "@mui/icons-material/Handshake";
import LogoutIcon from "@mui/icons-material/Logout";
import React, { useCallback, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { useAuth } from "../../context/connectionContext";
import { useUserData } from "../../context/userContext";
import CopyableTypography from "../../components/CopyableTypography";
import { truncateForDisplay } from "../../utils/textUtils";

const SETTING_ICONS: Record<string, React.ReactNode> = {
  "Manage Helpers": <PeopleIcon sx={{ fontSize: 20 }} />,
  "Manage Trustors": <HandshakeIcon sx={{ fontSize: 20 }} />,
  Logout: <LogoutIcon sx={{ fontSize: 20 }} />,
};

type TSetting = {
  label: string;
  action: () => void;
};

type props = {
  settings: TSetting[];
};

const CopyableMenuItem = ({
  label,
  text,
  fallbackText = "Not Available",
}: {
  label: string;
  text: string;
  fallbackText?: string;
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fullText = text || fallbackText;
  const displayText =
    isSmallScreen && fullText.length > 18 ? truncateForDisplay(fullText) : fullText;

  return (
    <MenuItem
      sx={{
        flexDirection: "column",
        alignItems: "flex-start",
        py: 2,
        px: 2,
        minHeight: 44,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <CopyableTypography
        text={fullText}
        displayText={displayText}
        variant="body2"
        color="text.primary"
        sxProps={{ fontWeight: 500 }}
      />
    </MenuItem>
  );
};

const UserMenu = ({ settings }: props) => {
  const { account, isConnected, providerClient } = useAuth();
  const { name } = useUserData();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const copyableMenuItems = [
    {
      label: "User Name",
      text: name,
    },

    {
      label: "Account Address",
      text: account,
    },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSettingClicked = useCallback(
    (setting: any) => {
      setting.action();
      handleCloseUserMenu();
    },
    [handleCloseUserMenu]
  );

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          <Avatar
            alt={name && isConnected ? name.toUpperCase() : "Wikey"}
            src="/static/images/avatar/2.jpg"
          />
        </IconButton>
      </Tooltip>
      {!!providerClient && isConnected && (
        <Menu
          sx={{ mt: "45px" }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
        >
          {copyableMenuItems.map((item) => (
            <CopyableMenuItem key={item.label} {...item} />
          ))}
          {settings.map((setting) => (
            <MenuItem
              key={setting.label}
              onClick={() => handleSettingClicked(setting)}
              sx={{ minHeight: 44, py: 1.5 }}
            >
              <Box component="span" sx={{ display: "flex", alignItems: "center", mr: 1.5 }}>
                {SETTING_ICONS[setting.label] ?? null}
              </Box>
              <Typography sx={{ textAlign: "center" }}>{setting.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default UserMenu;
